import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useChurchPlanner, usePlannerRoles } from '../../hooks/useChurchPlanner';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerInput,
  PlannerError,
  plannerColors,
} from './plannerTheme';

const HeaderCard = styled(PlannerCard)`
  padding: 1.2rem 1.25rem;
  background:
    radial-gradient(circle at top right, rgba(229, 211, 179, 0.16), transparent 32%),
    linear-gradient(180deg, #232326 0%, #19191b 100%);
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderCopy = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const HeaderMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StatPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.42rem 0.72rem;
  background: rgba(255, 255, 255, 0.03);
  color: ${plannerColors.text};
  font-size: 0.82rem;
  font-weight: 700;
`;

const SearchShell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 14px;
  background: ${plannerColors.surfaceHover};
  padding: 0.8rem 0.9rem;
  margin-top: 1rem;
`;

const FeedbackBanner = styled.div<{ $tone: 'success' | 'error' | 'info' }>`
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'success'
        ? 'rgba(74, 222, 128, 0.45)'
        : $tone === 'error'
          ? 'rgba(248, 113, 113, 0.45)'
          : 'rgba(229, 211, 179, 0.38)'};
  border-radius: 14px;
  padding: 0.85rem 1rem;
  background:
    ${({ $tone }) =>
      $tone === 'success'
        ? 'rgba(74, 222, 128, 0.09)'
        : $tone === 'error'
          ? 'rgba(248, 113, 113, 0.08)'
          : 'rgba(229, 211, 179, 0.08)'};
  color: ${plannerColors.text};
  font-size: 0.84rem;
  line-height: 1.4;
`;

const SearchIconBubble = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(229, 211, 179, 0.12);
  color: ${plannerColors.accent};
  flex-shrink: 0;
  font-weight: 800;
`;

const SearchInput = styled(PlannerInput)`
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0.1rem 0;
  outline: none;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const SelectionToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid ${plannerColors.border};
  margin-bottom: 0.85rem;
`;

const SelectionLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: ${plannerColors.text};
  font-size: 0.83rem;
  font-weight: 700;
`;

const SelectionCheckbox = styled.input`
  accent-color: ${plannerColors.accent};
`;

const SelectionMeta = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.79rem;
  font-weight: 600;
`;

const BulkActionStrip = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 14px;
  background: ${plannerColors.surfaceHover};
  padding: 0.85rem 0.95rem;
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BulkActionText = styled.p`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.84rem;
  font-weight: 700;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  border: 1px solid
    ${({ $variant }) => ($variant === 'primary' ? 'rgba(229, 211, 179, 0.6)' : plannerColors.border)};
  background:
    ${({ $variant }) =>
      $variant === 'primary'
        ? 'linear-gradient(180deg, rgba(229, 211, 179, 0.22), rgba(229, 211, 179, 0.08))'
        : plannerColors.surface};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.48rem 0.78rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GroupSection = styled.section`
  display: grid;
  gap: 0.65rem;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  border-bottom: 1px solid ${plannerColors.border};
  padding-bottom: 0.4rem;
`;

const GroupTitle = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const GroupCount = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.77rem;
  font-weight: 600;
`;

const PersonCard = styled(Link)`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.9rem;
  text-decoration: none;
  border: 1px solid ${plannerColors.border};
  border-radius: 16px;
  padding: 0.85rem 0.95rem;
  background: ${plannerColors.surfaceHover};
  transition: transform 140ms ease, border-color 140ms ease, background 140ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(229, 211, 179, 0.55);
    background: #2b2b2f;
  }

  @media (max-width: 720px) {
    align-items: flex-start;
  }
`;

const PersonRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.7rem;
`;

const RowSelectControl = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  min-height: 2rem;
`;

const AvatarFrame = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid rgba(229, 211, 179, 0.26);
  background: linear-gradient(180deg, rgba(229, 211, 179, 0.18), rgba(255, 255, 255, 0.04));
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: ${plannerColors.text};
  font-weight: 800;
`;

const PersonBody = styled.div`
  min-width: 0;
  display: grid;
  gap: 0.45rem;
`;

const Name = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.98rem;
  font-weight: 700;
`;

const HeaderLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const SecondaryLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.7rem;
  color: ${plannerColors.muted};
  font-size: 0.82rem;
  line-height: 1.35;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  border: 1px solid ${plannerColors.border};
  background: rgba(255, 255, 255, 0.03);
  color: ${plannerColors.text};
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const DetailRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const RoleChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 999px;
  padding: 0.32rem 0.56rem;
  border: 1px solid ${plannerColors.border};
  background: rgba(255, 255, 255, 0.03);
  color: ${plannerColors.text};
  font-size: 0.76rem;
  font-weight: 600;
`;

const RoleLabel = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const MetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.32rem 0.56rem;
  background: rgba(255, 255, 255, 0.025);
  color: ${plannerColors.text};
  font-size: 0.76rem;
  font-weight: 600;
`;

const MetaLabel = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.67rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 18, 21, 0.68);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.4rem;
  z-index: 200;
`;

const ModalCard = styled.div`
  width: min(980px, 100%);
  max-height: 88vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.35);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid ${plannerColors.border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 1.05rem;
  font-weight: 800;
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${plannerColors.muted};
  font-size: 1.1rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 1.1rem 1.15rem 1.2rem;
  overflow: auto;
  display: grid;
  gap: 1rem;
`;

const ModalGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const ModalColumn = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 16px;
  background: ${plannerColors.surfaceHover};
  padding: 0.9rem;
  display: grid;
  gap: 0.8rem;
`;

const ModalColumnTitle = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.92rem;
  font-weight: 800;
`;

const BulkSelect = styled.select`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
  color: ${plannerColors.text};
  border-radius: 12px;
  padding: 0.58rem 0.7rem;
  font-size: 0.86rem;
`;

const ModalHelper = styled.p`
  margin: 0;
  color: ${plannerColors.muted};
  font-size: 0.8rem;
  line-height: 1.45;
`;

const ModalRoleGroupList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const ModalRoleGroup = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const ModalRoleGroupTitle = styled.div`
  color: ${plannerColors.text};
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ModalRoleList = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const RoleToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: ${plannerColors.text};
  font-size: 0.82rem;
`;

function formatDate(value: string | null) {
  if (!value) return 'No service';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(parsed.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '--';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

const BULK_PRIMARY_NO_CHANGE = '__no_change__';
const BULK_PRIMARY_CLEAR = '__clear__';

export default function ChurchPlannerPeoplePage() {
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);
  const [search, setSearch] = useState('');
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  const [bulkPrimaryRoleValue, setBulkPrimaryRoleValue] = useState(BULK_PRIMARY_NO_CHANGE);
  const [bulkAddSecondaryRoleIds, setBulkAddSecondaryRoleIds] = useState<string[]>([]);
  const [bulkRemoveSecondaryRoleIds, setBulkRemoveSecondaryRoleIds] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const roleMetaById = useMemo(() => {
    const map = new Map<string, { name: string; area: string }>();
    (rolesQuery.data ?? []).forEach((role) => {
      if (role.id && role.name) {
        map.set(role.id, {
          name: role.name,
          area: (role.area || '').trim() || 'General',
        });
      }
    });
    return map;
  }, [rolesQuery.data]);

  const rolesByArea = useMemo(() => {
    const groups = new Map<string, { key: string; name: string; roles: Array<{ id: string; name: string; area?: string | null }> }>();
    (rolesQuery.data ?? []).forEach((role) => {
      const key = (role.area || '').trim().toLowerCase() || 'general';
      const name = (role.area || '').trim() || 'General';
      const existing = groups.get(key);
      if (existing) {
        existing.roles.push(role);
      } else {
        groups.set(key, { key, name, roles: [role] });
      }
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        roles: [...group.roles].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rolesQuery.data]);

  const filteredPeople = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return planner.people;
    return planner.people.filter((person) => {
      const statusText = person.statuses.join(' ');
      const roleText = person.roleNames.join(' ');
      return (
        person.name.toLowerCase().includes(query) ||
        (person.email || '').toLowerCase().includes(query) ||
        roleText.toLowerCase().includes(query) ||
        statusText.toLowerCase().includes(query)
      );
    });
  }, [planner.people, search]);

  const groupedPeople = useMemo(() => {
    const groups = new Map<string, { key: string; name: string; people: typeof filteredPeople }>();

    filteredPeople.forEach((person) => {
      const roleMeta = person.primaryPlannerRoleId ? roleMetaById.get(person.primaryPlannerRoleId) : null;
      const groupName = roleMeta?.area || 'No Primary Role';
      const key = groupName.toLowerCase();
      const existing = groups.get(key);
      if (existing) {
        existing.people.push(person);
      } else {
        groups.set(key, { key, name: groupName, people: [person] });
      }
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        people: [...group.people].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => {
        if (a.name === 'No Primary Role') return 1;
        if (b.name === 'No Primary Role') return -1;
        return a.name.localeCompare(b.name);
      });
  }, [filteredPeople, roleMetaById]);

  const visiblePersonIds = useMemo(() => filteredPeople.map((person) => person.id), [filteredPeople]);
  const allVisibleSelected = visiblePersonIds.length > 0 && visiblePersonIds.every((personId) => selectedPersonIds.includes(personId));
  const someVisibleSelected = !allVisibleSelected && selectedPersonIds.length > 0;
  const selectedBulkPrimaryRoleId =
    bulkPrimaryRoleValue !== BULK_PRIMARY_NO_CHANGE && bulkPrimaryRoleValue !== BULK_PRIMARY_CLEAR
      ? bulkPrimaryRoleValue
      : null;
  const hasBulkRoleChanges =
    bulkPrimaryRoleValue !== BULK_PRIMARY_NO_CHANGE ||
    bulkAddSecondaryRoleIds.length > 0 ||
    bulkRemoveSecondaryRoleIds.length > 0;

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected]);

  useEffect(() => {
    const visibleIdSet = new Set(visiblePersonIds);
    setSelectedPersonIds((prev) => prev.filter((personId) => visibleIdSet.has(personId)));
  }, [visiblePersonIds]);

  useEffect(() => {
    if (!selectedBulkPrimaryRoleId) return;
    setBulkAddSecondaryRoleIds((prev) => prev.filter((roleId) => roleId !== selectedBulkPrimaryRoleId));
  }, [selectedBulkPrimaryRoleId]);

  const togglePersonSelection = (personId: string) => {
    setSelectedPersonIds((prev) =>
      prev.includes(personId) ? prev.filter((existingId) => existingId !== personId) : [...prev, personId]
    );
  };

  const toggleSelectAllVisible = () => {
    setSelectedPersonIds((prev) => {
      if (allVisibleSelected) {
        const visibleIdSet = new Set(visiblePersonIds);
        return prev.filter((personId) => !visibleIdSet.has(personId));
      }
      return Array.from(new Set([...prev, ...visiblePersonIds]));
    });
  };

  const resetBulkRoleEditor = () => {
    setBulkPrimaryRoleValue(BULK_PRIMARY_NO_CHANGE);
    setBulkAddSecondaryRoleIds([]);
    setBulkRemoveSecondaryRoleIds([]);
  };

  const closeBulkRoleEditor = () => {
    if (planner.isMutating) return;
    resetBulkRoleEditor();
    setShowBulkRoleModal(false);
  };

  const openBulkRoleEditor = () => {
    if (!selectedPersonIds.length) {
      setBulkResult({ tone: 'info', text: 'Select at least one planner member on this page.' });
      return;
    }
    resetBulkRoleEditor();
    setShowBulkRoleModal(true);
  };

  const toggleBulkAddSecondaryRole = (roleId: string) => {
    if (roleId === selectedBulkPrimaryRoleId) return;
    setBulkAddSecondaryRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((existingRoleId) => existingRoleId !== roleId) : [...prev, roleId]
    );
    setBulkRemoveSecondaryRoleIds((prev) => prev.filter((existingRoleId) => existingRoleId !== roleId));
  };

  const toggleBulkRemoveSecondaryRole = (roleId: string) => {
    setBulkRemoveSecondaryRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((existingRoleId) => existingRoleId !== roleId) : [...prev, roleId]
    );
    setBulkAddSecondaryRoleIds((prev) => prev.filter((existingRoleId) => existingRoleId !== roleId));
  };

  const applyBulkRoleChanges = async () => {
    if (!selectedPersonIds.length || !hasBulkRoleChanges || planner.isMutating) return;

    const primaryRoleAction =
      bulkPrimaryRoleValue === BULK_PRIMARY_CLEAR
        ? 'clear'
        : bulkPrimaryRoleValue === BULK_PRIMARY_NO_CHANGE
          ? 'no_change'
          : 'set';

    try {
      const summary = await planner.bulkUpdateMemberRoles({
        userIds: selectedPersonIds,
        primaryRoleAction,
        primaryRoleId: primaryRoleAction === 'set' ? bulkPrimaryRoleValue : null,
        addSecondaryRoleIds: bulkAddSecondaryRoleIds,
        removeSecondaryRoleIds: bulkRemoveSecondaryRoleIds,
      });

      const updatedCount = summary.updatedUserIds.length;
      const failedCount = summary.failed.length;
      const skippedCount = summary.skippedUserIds.length;
      const firstFailure = summary.failed[0]?.message;

      setBulkResult({
        tone: failedCount > 0 ? (updatedCount > 0 ? 'info' : 'error') : updatedCount > 0 ? 'success' : 'info',
        text: `Bulk planner role update complete: ${updatedCount} updated, ${failedCount} failed, ${skippedCount} skipped.${firstFailure ? ` First error: ${firstFailure}` : ''}`,
      });

      if (failedCount === 0) {
        setSelectedPersonIds([]);
        closeBulkRoleEditor();
      }
    } catch (error) {
      setBulkResult({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Unable to apply bulk planner role changes.',
      });
    }
  };

  return (
    <PlannerPage>
      {bulkResult ? <FeedbackBanner $tone={bulkResult.tone}>{bulkResult.text}</FeedbackBanner> : null}
      <HeaderCard>
        <HeaderRow>
          <HeaderCopy>
            <PlannerKicker>Directory</PlannerKicker>
            <PlannerHeading>People</PlannerHeading>
            <PlannerSubtle>
              A cleaner directory view for planner members, role defaults, and secondary coverage.
            </PlannerSubtle>
          </HeaderCopy>
          <HeaderMetaRow>
            <StatPill>{planner.people.length} members</StatPill>
            <StatPill>{planner.people.filter((person) => !!person.primaryPlannerRoleId).length} with primary role</StatPill>
            <StatPill>{planner.people.filter((person) => person.secondaryPlannerRoleIds.length > 0).length} with secondary roles</StatPill>
          </HeaderMetaRow>
        </HeaderRow>

        <SearchShell>
          <SearchIconBubble>⌕</SearchIconBubble>
          <SearchInput
            type="search"
            value={search}
            placeholder="Search name, role, or status"
            onChange={(event) => setSearch(event.target.value)}
          />
        </SearchShell>

        {planner.isError ? <PlannerError>Unable to load people data right now.</PlannerError> : null}
      </HeaderCard>

      <PlannerCard>
        {planner.isLoading ? (
          <PlannerSubtle>Loading people…</PlannerSubtle>
        ) : filteredPeople.length === 0 ? (
          <PlannerSubtle>No people matched your search.</PlannerSubtle>
        ) : (
          <>
            <SelectionToolbar>
              <SelectionLabel>
                <SelectionCheckbox
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  disabled={planner.isMutating}
                  aria-label="Select all visible planner members"
                />
                <span>Select all visible</span>
              </SelectionLabel>
              <SelectionMeta>
                {selectedPersonIds.length} selected on this page
              </SelectionMeta>
            </SelectionToolbar>

            {selectedPersonIds.length > 0 ? (
              <BulkActionStrip>
                <BulkActionText>
                  {selectedPersonIds.length} planner member{selectedPersonIds.length === 1 ? '' : 's'} selected
                </BulkActionText>
                <ActionRow>
                  <ActionButton type="button" $variant="primary" onClick={openBulkRoleEditor} disabled={planner.isMutating}>
                    Bulk edit planner roles
                  </ActionButton>
                  <ActionButton type="button" onClick={() => setSelectedPersonIds([])} disabled={planner.isMutating}>
                    Clear selection
                  </ActionButton>
                </ActionRow>
              </BulkActionStrip>
            ) : null}

            <CardList>
              {groupedPeople.map((group) => (
                <GroupSection key={group.key}>
                  <GroupHeader>
                    <GroupTitle>{group.name}</GroupTitle>
                    <GroupCount>{group.people.length} members</GroupCount>
                  </GroupHeader>
                  {group.people.map((person) => {
                    const primaryRoleName = person.primaryPlannerRoleId
                      ? roleMetaById.get(person.primaryPlannerRoleId)?.name || 'Assigned'
                      : 'Not set';
                    const secondaryRoleNames = person.secondaryPlannerRoleIds
                      .map((roleId) => roleMetaById.get(roleId)?.name)
                      .filter((name): name is string => !!name);
                    const secondarySummary =
                      secondaryRoleNames.length <= 2
                        ? secondaryRoleNames.join(', ')
                        : `${secondaryRoleNames.length} roles`;

                    return (
                      <PersonRow key={person.id}>
                        <RowSelectControl>
                          <SelectionCheckbox
                            type="checkbox"
                            checked={selectedPersonIds.includes(person.id)}
                            onChange={() => togglePersonSelection(person.id)}
                            disabled={planner.isMutating}
                            aria-label={`Select ${person.name}`}
                          />
                        </RowSelectControl>
                        <PersonCard to={`/church-planner/people/${person.id}`}>
                          <AvatarFrame>
                            {person.avatar ? (
                              <AvatarImage src={person.avatar} alt={person.name} loading="lazy" />
                            ) : (
                              <AvatarFallback>{getInitials(person.name)}</AvatarFallback>
                            )}
                          </AvatarFrame>
                          <PersonBody>
                            <HeaderLine>
                              <Name>{person.name}</Name>
                              <StatusPill>{person.statuses.length ? person.statuses[0] : 'available'}</StatusPill>
                            </HeaderLine>
                            <SecondaryLine>
                              <span>{person.email || 'No email available'}</span>
                              {person.title ? <span>{person.title}</span> : null}
                            </SecondaryLine>

                            <DetailRow>
                              <RoleChip>
                                <RoleLabel>Primary</RoleLabel>
                                <span>{primaryRoleName}</span>
                              </RoleChip>
                              {secondaryRoleNames.length ? (
                                <RoleChip>
                                  <RoleLabel>Secondary</RoleLabel>
                                  <span>{secondarySummary}</span>
                                </RoleChip>
                              ) : null}
                              <MetaPill>
                                <MetaLabel>Assignments</MetaLabel>
                                <span>{person.assignmentsCount}</span>
                              </MetaPill>
                              <MetaPill>
                                <MetaLabel>Next</MetaLabel>
                                <span>{formatDate(person.nextServiceDate)}</span>
                              </MetaPill>
                            </DetailRow>
                          </PersonBody>
                        </PersonCard>
                      </PersonRow>
                    );
                  })}
                </GroupSection>
              ))}
            </CardList>
          </>
        )}
      </PlannerCard>

      {showBulkRoleModal ? (
        <ModalOverlay onClick={closeBulkRoleEditor}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>Bulk Edit Planner Roles</ModalTitle>
                <ModalHelper>
                  Apply primary and secondary role changes to {selectedPersonIds.length} selected planner member
                  {selectedPersonIds.length === 1 ? '' : 's'} on this page.
                </ModalHelper>
              </div>
              <ModalCloseButton type="button" onClick={closeBulkRoleEditor} disabled={planner.isMutating}>
                X
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalColumn>
                <ModalColumnTitle>Primary role</ModalColumnTitle>
                <BulkSelect
                  value={bulkPrimaryRoleValue}
                  onChange={(event) => setBulkPrimaryRoleValue(event.target.value)}
                  disabled={planner.isMutating}
                >
                  <option value={BULK_PRIMARY_NO_CHANGE}>No change</option>
                  <option value={BULK_PRIMARY_CLEAR}>Clear primary role</option>
                  {rolesByArea.map((group) => (
                    <optgroup key={group.key} label={group.name}>
                      {group.roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </BulkSelect>
                <ModalHelper>
                  Choosing a new primary role automatically removes that role from secondary selections during save.
                </ModalHelper>
              </ModalColumn>

              <ModalGrid>
                <ModalColumn>
                  <ModalColumnTitle>Secondary roles to add</ModalColumnTitle>
                  <ModalHelper>
                    Existing secondary roles stay as-is unless you explicitly remove them.
                  </ModalHelper>
                  <ModalRoleGroupList>
                    {rolesByArea.map((group) => (
                      <ModalRoleGroup key={`add-${group.key}`}>
                        <ModalRoleGroupTitle>{group.name}</ModalRoleGroupTitle>
                        <ModalRoleList>
                          {group.roles.map((role) => (
                            <RoleToggle key={`add-role-${role.id}`}>
                              <SelectionCheckbox
                                type="checkbox"
                                checked={bulkAddSecondaryRoleIds.includes(role.id)}
                                onChange={() => toggleBulkAddSecondaryRole(role.id)}
                                disabled={planner.isMutating || role.id === selectedBulkPrimaryRoleId}
                              />
                              <span>{role.name}</span>
                            </RoleToggle>
                          ))}
                        </ModalRoleList>
                      </ModalRoleGroup>
                    ))}
                  </ModalRoleGroupList>
                </ModalColumn>

                <ModalColumn>
                  <ModalColumnTitle>Secondary roles to remove</ModalColumnTitle>
                  <ModalHelper>
                    Only the checked secondary roles will be removed for the selected people.
                  </ModalHelper>
                  <ModalRoleGroupList>
                    {rolesByArea.map((group) => (
                      <ModalRoleGroup key={`remove-${group.key}`}>
                        <ModalRoleGroupTitle>{group.name}</ModalRoleGroupTitle>
                        <ModalRoleList>
                          {group.roles.map((role) => (
                            <RoleToggle key={`remove-role-${role.id}`}>
                              <SelectionCheckbox
                                type="checkbox"
                                checked={bulkRemoveSecondaryRoleIds.includes(role.id)}
                                onChange={() => toggleBulkRemoveSecondaryRole(role.id)}
                                disabled={planner.isMutating}
                              />
                              <span>{role.name}</span>
                            </RoleToggle>
                          ))}
                        </ModalRoleList>
                      </ModalRoleGroup>
                    ))}
                  </ModalRoleGroupList>
                </ModalColumn>
              </ModalGrid>

              {!hasBulkRoleChanges ? (
                <ModalHelper>Select at least one primary or secondary role change to apply.</ModalHelper>
              ) : null}

              <ActionRow>
                <ActionButton
                  type="button"
                  $variant="primary"
                  onClick={applyBulkRoleChanges}
                  disabled={!hasBulkRoleChanges || planner.isMutating}
                >
                  Apply bulk changes
                </ActionButton>
                <ActionButton type="button" onClick={closeBulkRoleEditor} disabled={planner.isMutating}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </PlannerPage>
  );
}
